import { Component, ElementRef, ViewChild, inject, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '@/apps/tasklist/service/auth.service';
import { AvatarModule } from "primeng/avatar";


@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, AppBreadcrumb, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, TooltipModule, FormsModule, AvatarModule],
    template: `<div class="layout-topbar">
        <div class="topbar-start">
            <button #menubutton type="button" class="topbar-menubutton p-link p-trigger hover:cursor-pointer" (click)="onMenuButtonClick()">
                <i class="pi pi-bars"></i>
            </button>
            <nav app-breadcrumb class="topbar-breadcrumb"></nav>
        </div>

        <div class="topbar-end">
            <ul class="topbar-menu">
                <!-- <li class="topbar-search">
                    <p-iconfield>
                        <p-inputicon class="pi pi-search" />
                        <input type="text" pInputText placeholder="Search tasks..." 
                               class="w-48 sm:w-full" 
                               [(ngModel)]="searchQuery"
                               (input)="onSearch($event)"
                               (keyup.enter)="onSearchEnter()" />
                        <p-inputicon *ngIf="searchQuery" 
                                   class="pi pi-times cursor-pointer ml-2" 
                                   (click)="clearSearch()"
                                   pTooltip="Clear search" />
                    </p-iconfield>
                </li> -->
                <li class="ml-3">
                    <p-button icon="pi pi-palette" rounded (onClick)="onConfigButtonClick()"></p-button>
                </li>
                
                <!-- Simple User Avatar -->
                <li class="topbar-profile" *ngIf="authService.currentUser$ | async as currentUser">
                    <button type="button" class="p-link hover:cursor-pointer" (click)="onProfileButtonClick()" 
                            pTooltip="Open Profile" tooltipPosition="bottom">
                          <p-avatar 
                                    [label]="(currentUser.displayName.charAt(0) || '?')" 
                                    size="large" 
                                    shape="circle" 
                                    [style]="{'background-color': '#10B981', 'color': 'white', 'margin-right': '0.5rem'}">
                                </p-avatar>
                    </button>
                </li>
                
                <!-- Fallback if no user is logged in -->
                <li class="topbar-profile" *ngIf="!(authService.currentUser$ | async)">
                    <button type="button" class="p-link hover:cursor-pointer" (click)="onProfileButtonClick()" 
                            pTooltip="Open Profile" tooltipPosition="bottom">
                        <img src="/layout/images/avatar.png" alt="Profile" class="w-8 h-8 rounded-full" />
                    </button>
                </li>
            </ul>
        </div>
    </div>`
})
export class AppTopbar implements OnDestroy {
    @ViewChild('menubutton') menuButton!: ElementRef;

    searchQuery: string = '';
    private searchSubject = new Subject<string>();

    constructor(
      
        public layoutService: LayoutService,
        public authService: AuthService,
        private router: Router
    ) {
        // Set up search debouncing with 1 second delay
        this.searchSubject.pipe(
            debounceTime(1000), // Wait 1 second after user stops typing
            distinctUntilChanged() // Only emit if value is different from previous
        ).subscribe(query => {
            this.performSearch(query);
        });
    }

    ngOnDestroy(): void {
        this.searchSubject.complete();
    }

    // Search functionality
    onSearch(event: any): void {
        const query = event.target.value;
        this.searchQuery = query;
        
        if (query.length === 0) {
            // Clear search immediately when input is emptied
            this.clearSearch();
        } else {
            // Use debounced search for all other cases
            this.searchSubject.next(query);
            
        }
    }

    onSearchEnter(): void {
        if (this.searchQuery.trim()) {
            this.performSearch(this.searchQuery);
        }
    }

    private performSearch(query: string): void {
        // Only search if query has minimum length
        if (query.length < 2) {
            return;
        }
      
        // Use Angular router to navigate instead of window.location to avoid page reload
        this.router.navigate(['/apps/tasklist'], { 
            queryParams: { search: query },
            queryParamsHandling: 'merge'
        }).catch(() => {
            // Fallback to current page search if navigation fails
            console.log('Searching for:', query);
        });
    }

    clearSearch(): void {
        this.searchQuery = '';
        // Clear any pending search in the debounce queue
        this.searchSubject.next('');
        // Navigate to task list without search query parameter
        this.router.navigate(['/apps/tasklist'], { 
            queryParams: { search: null },
            queryParamsHandling: 'merge'
        });
    }

    // Helper methods for template type safety
    getDisplayName(user: any): string {
        return user?.displayName || user?.name || 'Unknown User';
    }

    getEmail(user: any): string {
        return user?.email || user?.mail || 'No email';
    }

    getAvatar(user: any): string {
        // Provide a more robust avatar system
        const avatar = user?.avatar || 'avatar.png';
        // If the avatar doesn't exist, we'll let the browser handle the fallback
        return avatar;
    }

    onAvatarError(event: any): void {
        // Fallback to default avatar if image fails to load
        event.target.src = '/demo/images/avatar/avatar.png';
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    onProfileButtonClick() {
        this.layoutService.showProfileSidebar();
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }
}
